import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { clearAuth, getCurrentUser, getCurrentUserRole, getNeedChangePassword, getToken, setCurrentUser } from '@/utils/auth'
import { menuSections } from '@/config/menu'

const resolvedBaseURL = process.env.VUE_APP_BASE_API
  || (process.env.NODE_ENV === 'development' ? 'http://localhost:8888' : '')

async function fetchCurrentUser(token) {
  const response = await fetch(`${resolvedBaseURL}/api/v1/user/me`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
  if (!response.ok) {
    throw new Error(`fetch /me failed: ${response.status}`)
  }
  return response.json()
}

const routes = [
  { path: '/login', name: 'login', component: () => import('../views/LoginView.vue'), meta: { layout: 'empty' } },
  { path: '/change-password', name: 'forceChangePassword', component: () => import('../views/ForceChangePassword.vue'), meta: { layout: 'empty' } },
  { path: '/403', name: 'forbidden', component: () => import('../views/ForbiddenView.vue'), meta: { layout: 'empty' } },
  { path: '/', name: 'home', component: HomeView },

  // === 核心：快速开单台 ===
  { path: '/orders/new', name: 'quickOrder', component: () => import('../views/QuickOrder.vue') },
  { path: '/orders/new/legacy', name: 'quickOrderLegacy', component: () => import('../views/QuickOrderV1.vue') },

  // === 线上接单工作台 ===
  { path: '/online-orders', name: 'onlineOrders', component: () => import('../views/OnlineOrders.vue') },

  // === 销售管理 ===
  { path: '/orders', name: 'orders', component: () => import('../views/OrderManagement.vue') },
  { path: '/orders/return', name: 'returnOrderList', component: () => import('../views/OrderManagement.vue') },
  { path: '/orders/return/verify', name: 'returnVerifySearch', component: () => import('../views/ReturnVerifySearch.vue') },
  { path: '/orders/return/new', name: 'returnOrder', component: () => import('../views/ReturnOrder.vue') },

  // === 进货管理 ===
  { path: '/purchase', name: 'purchase', component: () => import('../views/PurchaseManagement.vue') },
  { path: '/purchase/new', name: 'purchaseCreate', component: () => import('../views/PurchaseCreate.vue') },
  { path: '/purchase/rma', name: 'purchaseRmaList', component: () => import('../views/PurchaseRmaList.vue') },
  { path: '/purchase/rma/create', name: 'purchaseRmaCreate', component: () => import('../views/PurchaseRmaCreate.vue') },
  { path: '/suppliers', name: 'suppliers', component: () => import('../views/SupplierManagement.vue') },

  // === 售后工单 ===
  { path: '/aftersale', name: 'aftersale', component: () => import('../views/AftersaleManagement.vue') },

  // === 员工与考勤 ===
  { path: '/staff/operators', name: 'staffOperators', component: () => import('../views/OperatorManagement.vue') },
  { path: '/attendance', name: 'attendance', component: () => import('../views/AttendanceManagement.vue') },

  // === 配送管理 ===
  { path: '/delivery', name: 'delivery', component: () => import('../views/DeliveryManagement.vue') },
  { path: '/delivery/routes', name: 'deliveryRoutes', component: () => import('../views/DeliveryRouteManagement.vue') },

  // === 库存 ===
  { path: '/parts', name: 'parts', component: () => import('../views/PartsManagement.vue') },
  { path: '/part-categories', name: 'partCategories', component: () => import('../views/PartCategoryManagement.vue') },
  { path: '/warehouse', name: 'warehouse', component: () => import('../views/WarehouseManagement.vue') },
  { path: '/warehouse/bins', name: 'storageBins', component: () => import('../views/StorageBinManagement.vue') },
  { path: '/inventory/stocktake', name: 'inventoryStocktake', component: () => import('../views/InventoryStocktake.vue') },
  { path: '/inventory/barcodes', name: 'barcodeBinding', component: () => import('../views/BarcodeBindingWorkbench.vue') },

  // === 客户 ===
  { path: '/customers', name: 'customers', component: () => import('../views/CustomerManagement.vue') },

  // === 财务对账 ===
  { path: '/finance/daily', name: 'financeDaily', component: () => import('../views/FinanceDailyReconcile.vue') },
  { path: '/finance/monthly', name: 'financeMonthly', component: () => import('../views/MonthlyReconciliation.vue') },
  { path: '/finance/ar', name: 'financeAr', component: () => import('../views/CustomerAR.vue') },
  { path: '/finance/profit', name: 'financeProfit', component: () => import('../views/ProfitDashboard.vue') },
  { path: '/finance/legacy', name: 'financeLegacy', component: () => import('../views/FinanceLegacyInit.vue') },

  // === 系统 ===
  { path: '/settings', name: 'settings', component: () => import('../views/SystemSettings.vue'), meta: { roles: ['admin'] } },
  { path: '/settings/users', name: 'settingsUsers', component: () => import('../views/UserManagement.vue'), meta: { roles: ['admin'] } },
  { path: '/settings/roles', name: 'settingsRoles', component: () => import('../views/EmptyRolePermission.vue'), meta: { roles: ['admin'] } },
  { path: '/settings/mini-wechat', name: 'settingsMiniWechat', component: () => import('../views/MiniWechatApplications.vue'), meta: { roles: ['admin'], auth: 'api:GET:/api/v1/admin/mini/wechat/applications' } },

  // === 车型工作台（合并原 车型库 + 数据治理中枢 + 知识库）===
  { path: '/vehicle-models', name: 'vehicleModels', component: () => import('../views/VehicleModels.vue') },

  // === 智配治理 ===
  { path: '/fitment', name: 'fitment', component: () => import('../views/FitmentDashboard.vue') },
  { path: '/fitment/shadow', name: 'fitmentShadow', component: () => import('../views/ShadowFitmentInsights.vue') },
  { path: '/fitment/categories', name: 'categoryGovernance', component: () => import('../views/CategoryGovernance.vue') },

  // === 手机扫码 ===
  { path: '/scan', name: 'mobileScan', component: () => import('../views/MobileScanPage.vue'), meta: { layout: 'empty' } },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

let navStartMs = 0
const routerPerfEnabled = (() => {
  try {
    return typeof window !== 'undefined' && window?.localStorage?.getItem('ROUTER_PERF') === '1'
  } catch (_) {
    return false
  }
})()

function hasRoutePermission(requiredAuth, user) {
  if (!requiredAuth) return true
  const perms = Array.isArray(user?.perms) ? user.perms.map(p => String(p || '').trim()) : []
  if (perms.includes('*')) return true
  const required = String(requiredAuth || '').trim()
  if (!required) return true
  if (perms.includes(required)) return true
  // 支持 api:GET:/api/orders/* 匹配 api:GET:/api/orders/123
  return perms.some((p) => {
    if (!p.endsWith('/*')) return false
    const prefix = p.slice(0, -1)
    return required.startsWith(prefix)
  })
}

function getFirstAccessiblePath(user, role) {
  const normalizedRole = String(role || '').trim().toLowerCase()
  const roleAllowed = (roles) => {
    if (!Array.isArray(roles) || roles.length === 0) return true
    return roles.map(r => String(r).trim().toLowerCase()).includes(normalizedRole)
  }
  for (const section of menuSections) {
    for (const item of (section.items || [])) {
      if (!item?.path) continue
      if (item.path === '/') continue
      if (!roleAllowed(item.roles)) continue
      if (!hasRoutePermission(item.auth, user)) continue
      return item.path
    }
  }
  return '/403'
}

router.beforeEach(async (to, from, next) => {
  navStartMs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()
  if (routerPerfEnabled) console.log('[RouterPerf] beforeEach:start', { from: from.fullPath, to: to.fullPath })
  const token = getToken()

  if (to.path === '/login') {
    if (token) {
      next({ path: to.query?.redirect || '/' })
      return
    }
    next()
    return
  }

  if (to.path === '/403') {
    next()
    return
  }

  if (!token) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  if (getNeedChangePassword() && to.path !== '/change-password') {
    next({ path: '/change-password' })
    return
  }

  let role = getCurrentUserRole()
  let user = getCurrentUser() || {}
  if (!role) {
    try {
      user = await fetchCurrentUser(token)
      setCurrentUser(user)
      role = String(user?.role || '').trim().toLowerCase()
    } catch (e) {
      clearAuth()
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }
  } else {
    user = getCurrentUser() || {}
  }

  // 首页按可配置权限控制：无 ui:dashboard:view 时自动跳转到首个可访问页面
  if (to.path === '/' && !hasRoutePermission('ui:dashboard:view', user)) {
    const target = getFirstAccessiblePath(user, role)
    if (target !== to.path) {
      next({ path: target })
      return
    }
  }

  const requiredRoles = to.meta?.roles
  if (Array.isArray(requiredRoles) && requiredRoles.length > 0) {
    const allowed = requiredRoles.map(r => String(r).trim().toLowerCase())
    if (!allowed.includes(role)) {
      next({ path: '/403', query: { from: to.fullPath } })
      return
    }
  }

  const requiredAuth = to.meta?.auth
  if (requiredAuth && !hasRoutePermission(requiredAuth, user)) {
    next({ path: '/403', query: { from: to.fullPath } })
    return
  }

  next()
})

router.afterEach((to, from) => {
  const end = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()
  const costMs = navStartMs > 0 ? Math.round(end - navStartMs) : -1
  if (routerPerfEnabled) console.log('[RouterPerf] afterEach:done', { from: from.fullPath, to: to.fullPath, costMs })
})

export default router
